'use strict';

import {createElWithAttrs} from '@borngroup/born-utilities';
import {updateAvailability} from './availabilityUtils';
import {updateAddToCart} from './addToCart';
import {updateWishlistTriggers} from '../wishlist/wishlistUtils';
import {updateAddEngrav, updateAddEmboss, updateBraceletAdjustment} from './productPersonalisation';
import * as progress from '../components/progress';
import * as productUtils from './productUtils';
import {removeParamFromURL, updateHistoryState} from '../helpers/urlUtilities';
import {additionalStrapSelection} from '*/additionalStrap';

/**
 * Generates html for promotions section
 *
 * @param {array} promotions - list of promotions
 * @return {string} - Compiled HTML
 */
function _updatePromotions(promotions, productContainer) {
    let promotionsEl = productContainer.querySelector('[data-product-component="promotions"]');

    if (promotions && promotionsEl) {
        promotionsEl.textContent = '';

        promotions.forEach(function(promotion) {
            let promotionEl = createElWithAttrs(promotionsEl, {class: 'product-promotions__item', title: promotion.details}, 'li');

            promotionEl.textContent = promotion.calloutMsg;
        });
    }
}

/**
 * Updates the social sharing links for a given `productContainer`.
 * @param  {[type]} shareURLs        [description]
 * @param  {[type]} productContainer [description]
 * @return {[type]}                  [description]
 */
function _updateSocialShare(shareURLs, productContainer) {
    if (shareURLs) {
        let socialShareItems = productContainer.querySelectorAll('[data-social-share]');

        [].forEach.call(socialShareItems, function(currentItem) {
            let shareType = currentItem.getAttribute('data-social-share');

            currentItem.href = shareURLs[shareType];
        });
    }
}

/**
 * Generates html for product attributes section
 *
 * @param {array} attributes - list of attributes
 * @return {string} - Compiled HTML
 */
function _updateMainAttributes(attributes, productContainer) {
    let mainAttributesEl = productContainer.querySelector('[data-product-component="main-attributes"]');

    if (attributes && mainAttributesEl) {
        mainAttributesEl.textContent = '';

        attributes.forEach(function(attributeGroup) {
            if (attributeGroup.ID === 'mainAttributes') {
                attributeGroup.attributes.forEach(function(attribute) {
                    let attributeEl = createElWithAttrs(mainAttributesEl, {class: 'attribute-values'});

                    attributeEl.textContent = `${attribute.label}: ${attribute.value}`;
                });
            }
        });
    }
}

/**
 * Routes the handling of attribute processing depending on whether the attribute has image
 *     swatches or not
 *
 * @param {Object} attributes - Attribute
 * @param {string} attr.id - Attribute ID
 * @param {HTMLElement} productContainer - DOM element for a given product
 */
function _updateVariantAttributes(attributes, productContainer) {
    attributes.forEach(function(attr) {
        let matchingAttrGroupEl = productContainer.querySelector(`[data-attr-group="${attr.id}"]`);

        if (matchingAttrGroupEl && matchingAttrGroupEl.getAttribute('data-attr-group-type') === 'anchor') {
            _processAnchorAttributes(attr, productContainer);
        } else {
            _processDropdownAttributes(attr, productContainer);
        }
    });
}

/**
 * Process the attribute values for an attribute that has image swatches
 *
 * @param {Object} attr - Attribute
 * @param {string} attr.id - Attribute ID
 * @param {Object[]} attr.values - Array of attribute value objects
 * @param {string} attr.values.value - Attribute coded value
 * @param {string} attr.values.url - URL to de/select an attribute value of the product
 * @param {boolean} attr.values.isSelectable - Flag as to whether an attribute value can be
 *     selected.  If there is no variant that corresponds to a specific combination of attribute
 *     values, an attribute may be disabled in the Product Detail Page
 * @param {HTMLElement} productContainer - DOM container for a given product
 */
function _processAnchorAttributes(attr, productContainer) {
    let selectedAttr;

    attr.values.forEach(function(attrValue) {
        let attrValueEl = productContainer.querySelector(`[data-attr="${attr.id}"][data-attr-value="${attrValue.value}"]`);

        if (attrValueEl) {
            let attrValueIsAnchor = attrValueEl.tagName === 'A';

            if (attrValue.selected) {
                attrValueEl.classList.add('selected');

                selectedAttr = attrValue;

                attrValueEl.setAttribute('title', attrValue.labelSelected);
            } else {
                attrValueEl.classList.remove('selected');

                attrValueEl.setAttribute('title', attrValue.selectable ? attrValue.labelDefault : attrValue.labelUnselectable);
            }

            if (attrValue.url) {
                if (attrValueIsAnchor) {
                    attrValueEl.href = attrValue.url;
                } else {
                    attrValueEl.setAttribute('data-url', attrValue.url);
                }
            } else {
                attrValueEl.removeAttribute('href');
                attrValueEl.removeAttribute('data-url');
            }

            // Disable if not selectable
            attrValueEl.classList.remove('selectable');
            attrValueEl.classList.remove('unselectable');

            attrValueEl.classList.add(attrValue.selectable ? 'selectable' : 'unselectable');

            attrValueEl.setAttribute('aria-pressed', attrValue.selected);
            attrValueEl.setAttribute('aria-label', attrValue.selectable ? attrValue.labelDefault : attrValue.labelUnselectable);
        }
    });

    _updateSelectedAttributeValues(attr, selectedAttr, productContainer);
}

/**
 * Process attribute values associated with an attribute that does not have image swatches
 *
 * @param {Object} attr - Attribute
 * @param {string} attr.id - Attribute ID
 * @param {Object[]} attr.values - Array of attribute value objects
 * @param {string} attr.values.value - Attribute coded value
 * @param {string} attr.values.url - URL to de/select an attribute value of the product
 * @param {boolean} attr.values.isSelectable - Flag as to whether an attribute value can be
 *     selected.  If there is no variant that corresponds to a specific combination of attribute
 *     values, an attribute may be disabled in the Product Detail Page
 * @param {HTMLElement} productContainer - DOM container for a given product
 */
function _processDropdownAttributes(attr, productContainer) {
    let selectedAttr,
        attrQuery = `[data-attr="${attr.id}"]`,
        defaultOptionEl = productContainer.querySelector(attrQuery).options[0];

    defaultOptionEl.value = attr.resetUrl;

    attr.values.forEach(function(attrValue) {
        // Override for Denali
        if (window.SFRA.Denali && !attrValue.visibleWeb) {
            return;
        }

        let attrValueEl = productContainer.querySelector(`${attrQuery} [data-attr-value="${attrValue.value}"]`);

        attrValueEl.value = attrValue.url;

        if (attrValue.selected) {
            selectedAttr = attrValue;
        }
    });

    _updateSelectedAttributeValues(attr, selectedAttr, productContainer);
}

/**
 * Updates matching `[data-attr-<TYPE>-value]` elements with the selected attribute value.
 */
function _updateSelectedAttributeValues(attr, selectedAttr, productContainer) {
    let matchingTargetLabel = productContainer.querySelectorAll(`[data-attr-${attr.id}-value]`);

    [].forEach.call(matchingTargetLabel, function(currentEl) {
        currentEl.textContent = selectedAttr ? selectedAttr.displayValue : '';
    });
}


/**
 * Parses JSON from Ajax call made whenever an attribute value is [de]selected
 * @param {Object} response - response from Ajax call
 * @param {Object} response.product - Product object
 * @param {string} response.product.id - Product ID
 * @param {Object[]} response.product.variationAttributes - Product attributes
 * @param {Object[]} response.product.images - Product images
 * @param {boolean} response.product.hasRequiredAttrsSelected - Flag as to whether all required
 *     attributes have been selected.  Used partially to
 *     determine whether the Add to Cart button can be enabled
 * @param {jQuery} productContainer - DOM element for a given product.
 */
function _handleVariantResponse(response, productContainer) {
    let isChoiceOfBonusProducts = productContainer.closest('.choose-bonus-product-dialog');
    let selectedSize = response.product.size,
        dropdownAttributeEls = document.querySelectorAll('[data-attr-type="dropdown"]');

    [].forEach.call(dropdownAttributeEls, currentAction => {
        currentAction.setAttribute('data-selected-size', selectedSize);
        let pdpAccSelectSize = document.querySelector('.pdp-select-size');
        if (pdpAccSelectSize) {
            if (currentAction.getAttribute('data-selected-size').length > 0) {
                pdpAccSelectSize.classList.add('size-selected');
            } else {
                pdpAccSelectSize.classList.remove('size-selected');
            }
        }
    });

    if (response.product && response.product.variationAttributes) {
        let isVariant = response.product.productType === 'variant';

        _updateVariantAttributes(response.product.variationAttributes, productContainer);

        if (isChoiceOfBonusProducts && isVariant) {
            let bonusProductEl = productContainer.closest('[data-product-component="bonus-product"]');

            bonusProductEl.setAttribute('data-pid', response.product.id);

            bonusProductEl.setAttribute('data-ready-to-order', response.product.readyToOrder);
        }
    }

    // Update pricing
    if (!isChoiceOfBonusProducts) {
        productUtils.updateProductPrice(productContainer, response.product);
    }

    if (isChoiceOfBonusProducts) {
        let selectButton = productContainer.querySelector('[data-product-component="select-bonus"]');

        $(selectButton).trigger('bonusproduct:updateSelectButton', {
            product: response.product, container: productContainer
        });
    } else {
        // Fire "Add to Cart" button if all required attributes have been selected
        $('[data-product-component="add-button"]').trigger('product:updateAddToCart', {
            product: response.product, container: productContainer
        }).trigger('product:statusUpdate', response.product);
    }
}

/**
 * Updates DOM using post-option selection Ajax response
 *
 * @param {OptionSelectionResponse} options - Ajax response options from selecting a product option
 * @param {jQuery} productContainer - DOM element for current product
 */
function _updateOptions(options, productContainer) {
    options.forEach(function(option) {
        let optionEl = productContainer.querySelector(`[data-option-id*="${option.id}"]`);

        if (optionEl) {
            option.values.forEach(function(value) {
                let valueEl = optionEl.querySelector(`[data-value-id*="${value.id}"]`);

                if (valueEl) {
                    valueEl.value = value.url;
                }
            });
        }
    });
}

/**
 * Updates the quantity DOM elements post Ajax call
 * @param {UpdatedQuantity[]} quantities -
 * @param {jQuery} productContainer - DOM container for a given product
 */
function _updateQuantities(quantities, productContainer) {
    if (!productContainer.closest('[data-product-component="bonus-product"]')) {
        let quantityEl = productUtils.getQuantitySelector(productContainer);

        if (quantityEl) {
            quantityEl.textContent = '';

            quantities.forEach(function(quantity) {
                let optionEl = createElWithAttrs(quantityEl, {value: quantity.value, 'data-url': quantity.url}, 'option');

                if (quantity.selected) {
                    optionEl.setAttribute('selected', 'selected');
                }

                optionEl.textContent = quantity.value;
            });
        }
    }
}

function handleResponseVariation(responseData, responseProductContainer) {
    _handleVariantResponse(responseData, responseProductContainer);
    _updateOptions(responseData.product.options, responseProductContainer);
    _updateQuantities(responseData.product.quantities, responseProductContainer);
    _updatePromotions(responseData.product.promotions, responseProductContainer);
    _updateMainAttributes(responseData.product.attributes, responseProductContainer);
    _updateSocialShare(responseData.product.shareURLs.presentationList, responseProductContainer);
    _updateStockNotifyURL(responseData.product.id, responseProductContainer);
    updateAvailability(responseData, responseProductContainer);
    updateAddEngrav(responseData.product, responseProductContainer);
    updateAddEmboss(responseData.product, responseProductContainer);
    updateBraceletAdjustment(responseData.product, responseProductContainer);
    updateAddToCart(responseData.product, responseProductContainer);
    updateWishlistTriggers(responseProductContainer, !responseData.product.inWishlist);
    productUtils.updateImages(responseData.product.images.large, responseProductContainer, undefined, true);
    productUtils.updatePIDs(responseData.product.id, responseProductContainer);
    productUtils.updateURLs(responseData.queryString, responseProductContainer);
    productUtils.updateQueryStrings(responseData.queryString, responseProductContainer);
    productUtils.updateName(responseData.product.productDisplayName, responseProductContainer);
    $('body').trigger('product:afterAttributeSelect', {data: responseData, container: responseProductContainer});
}

/**
 * updates the product view when a product attribute is selected or deselected or when
 *         changing quantity
 * @param {string} selectedValueUrl - the Url for the selected variation value
 * @param {jQuery} productContainer - DOM element for current product
 */
function _attributeSelect(selectedValueUrl, productContainer) {
    if (selectedValueUrl) {
        let loaderTarget = productContainer.querySelector('[data-product-component="actions"]') || productContainer;

        $('body').trigger('product:beforeAttributeSelect', {url: selectedValueUrl, container: productContainer});

        progress.showLoader(loaderTarget);

        $.ajax({
            url: selectedValueUrl,
            method: 'GET',
            success: function(data) {
                if (data && data.product) {
                    if (data.product.variationAttributes && data.product.variationAttributes[0].selectedValue) {
                        updateHistoryState(false, data.product.selectedProductUrl);
                    }

                    handleResponseVariation(data, productContainer)
                }

                progress.hideLoader();
            },
            error: progress.hideLoader
        });
    }
}

function resetDataUrl(el) {
    let resetValue = el.value;
    if (resetValue.indexOf('size=') > -1 && el.options[el.selectedIndex].getAttribute('data-url') ) {
        if (resetValue.indexOf('size=&') < 0) {
            resetValue = resetValue.replace('size=', 'size=&');
        }
        el.options[el.selectedIndex].setAttribute('data-url', resetValue);
    }
}

export function requestProductUpdate(reqEvnt) {
    let isSelectSize = false;
    if (this.options && this.selectedIndex != null) { // when this.selectedIndex is equal to 0 the condition is fallig so add the null check
        isSelectSize = this.options[this.selectedIndex].getAttribute('data-isselect-size') ? this.options[this.selectedIndex].getAttribute('data-isselect-size') == 'true' : false;
    }

    if (this.selectedIndex == 0 && isSelectSize) {
        resetDataUrl(this);
    }

    let productContainerEle = productUtils.getProductContainer(this),
        selectedValue = this.href || (this.options ? this.options[this.selectedIndex].getAttribute('data-url') : false) || this.value || this.getAttribute('data-url');

    if (reqEvnt) {
        reqEvnt.preventDefault();
    }

    if (this.disabled) {
        return;
    }

    // Override for Denali
    if (window.SFRA.Denali && this.hasAttribute('data-denali-variant-select') && this.selectedIndex == 0) {
        let boutiqueSelect = productContainerEle.querySelector('[data-denali-boutique-select]');

        this.options[0].value = '';

        boutiqueSelect.options.length = 1;
        boutiqueSelect.focus();

        productContainerEle.querySelector('[data-locator-component="result-list"]').textContent = '';
        productContainerEle.querySelector('[data-denali-boutique-action]').classList.add('disabled');
        productContainerEle.querySelector('[data-focusout-element]').focus();
    }

    _attributeSelect(selectedValue, productContainerEle);
}

export function requestProductUpdateStrapColor() {
    let el = document.querySelector('[data-strap]'),
        pdpMain = document.querySelector('[data-strap-color]'),
        selectAttr = $('.pdp-main .item-info__personalization-container');

    pdpMain.classList.add('opacity');
    el.classList.add('opacity');
    if ($(selectAttr).hasClass('opacity')) {
        $('.product-gallery--pdp').addClass('opacity');
        $('.pdp').addClass('opacity');
    } else {
        $('.product-gallery--pdp').removeClass('opacity');
        $('.pdp').removeClass('opacity');
    }
}

additionalStrapSelection();

export function setupVariationListeners(container) {
    let containerEl = container || document,
        anchorAttributeEls = containerEl.querySelectorAll('[data-attr-type="anchor"]'),
        dropdownAttributeEls = containerEl.querySelectorAll('[data-attr-type="dropdown"]'),
        qtyAttributeEls = containerEl.querySelectorAll('[data-product-component="qty"]'),
        tabStrapColor = containerEl.querySelectorAll('[data-tab-id="tab-strap-color"]');

    [].forEach.call(tabStrapColor, currentAction => currentAction.addEventListener('click', requestProductUpdateStrapColor));
    [].forEach.call(anchorAttributeEls, currentAction => currentAction.addEventListener('click', requestProductUpdate));
    [].forEach.call(dropdownAttributeEls, currentAction => currentAction.addEventListener('change', requestProductUpdate));
    [].forEach.call(qtyAttributeEls, currentAction => currentAction.addEventListener('change', requestProductUpdate));
}

export function _updateStockNotifyURL(newID, productContainer) {
    let urlEls = productContainer.querySelectorAll('[data-notification-url]');

    [].forEach.call(urlEls, function(currentEl) {
        let updateURL = currentEl.getAttribute('data-url'),
            redURL = removeParamFromURL(updateURL, 'pid'),
            updatedURL = redURL + 'pid=' + newID;
        currentEl.setAttribute('data-url', updatedURL);
    });
}

export function afterAttributeSelect() {
    $('body').on('product:afterAttributeSelect', function(evt, response) {
        let isPDP = response.container && productUtils.getProductType(response.container) === 'pdp';

        if (isPDP) {
            updateHistoryState(window.location.href, response.data.product.selectedProductUrl);
        }
    });
}
